import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWalletClient } from 'wagmi';
import { OFT_ABI } from '../config/oft';
import { DEPOSIT_CHAIN_BY_ID, INK_DEPOSIT_CHAIN } from '../config/chains';
import { getNativeDropAmount } from '../utils/buildExtraOptions';

export interface OftSendParams {
  /** Amount to send in local decimals (6 for USDT0) */
  amountLD: bigint;
  /** Minimum amount to receive (from quoteOFT) — dust protection */
  minAmountLD: bigint;
  /** Native fee from quoteSend (added as msg.value) */
  nativeFee: bigint;
  /** LZ token fee from quoteSend (usually 0) */
  lzTokenFee: bigint;
  /** Extra options bytes (from buildExtraOptions) */
  extraOptions: `0x${string}`;
  /** Optional gas airdrop included in extra options */
  gasAirdrop?: { amount: bigint; recipient: `0x${string}` };
}

/**
 * Returns a mutation function to execute an OFT `send()` cross-chain transfer.
 *
 * Constructs the full SendParam including:
 * - dstEid: Ink's LayerZero EID (30339)
 * - to: user's address left-padded to bytes32
 * - amountLD: amount to send
 * - minAmountLD: dust-adjusted minimum (from quoteOFT)
 * - extraOptions: lzReceive gas + optional native drop
 *
 * msg.value = nativeFee + nativeDropAmount
 *
 * The wallet must already be connected and on the source chain when this mutation is called.
 *
 * @param sourceChainId - EVM chain ID of the source chain
 */
export function useOftSend(sourceChainId: number | undefined) {
  const { data: walletClient } = useWalletClient({
    chainId: sourceChainId,
  });
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: OftSendParams) => {
      if (!sourceChainId) {
        throw new Error('sourceChainId is required');
      }
      if (!walletClient) {
        throw new Error('Wallet not connected');
      }

      const chainConfig = DEPOSIT_CHAIN_BY_ID[sourceChainId];
      if (!chainConfig) {
        throw new Error(`No chain config for chainId ${sourceChainId}`);
      }

      const userAddress = walletClient.account.address;

      // Convert address to bytes32 (left-pad with zeros)
      // EVM address is 20 bytes, bytes32 is 32 bytes — pad with 12 zero bytes on left
      const addressAsBytes32 = ('0x' +
        '0000000000000000000000000000000000000000000000'.slice(0, 24) +
        userAddress.slice(2).toLowerCase()) as `0x${string}`;

      const sendParam = {
        dstEid: INK_DEPOSIT_CHAIN.lzEndpointId,
        to: addressAsBytes32,
        amountLD: params.amountLD,
        minAmountLD: params.minAmountLD,
        extraOptions: params.extraOptions,
        composeMsg: '0x' as `0x${string}`,
        oftCmd: '0x' as `0x${string}`,
      };

      // quoteSend() already includes the native drop cost in nativeFee
      // when extraOptions contains a nativeDrop option. Pass nativeFee
      // as both the fee param and msg.value — do NOT add nativeDropAmount
      // on top or the OFT reverts with NotEnoughNative.
      const fee = {
        nativeFee: params.nativeFee,
        lzTokenFee: params.lzTokenFee,
      };

      const txHash = await walletClient.writeContract({
        address: chainConfig.oftAddress,
        abi: OFT_ABI,
        functionName: 'send',
        args: [sendParam, fee, userAddress],
        value: params.nativeFee,
        chain: walletClient.chain,
        account: walletClient.account,
      });

      return txHash;
    },
    onSuccess: (txHash) => {
      // Invalidate token balance after successful send
      queryClient.invalidateQueries({
        queryKey: ['sourceTokenBalance', sourceChainId],
      });
      return txHash;
    },
  });
}
